import React, { useState } from 'react';
import Select from '../../components/Select';
import DatePickerCalendar from '../../components/DatePickerCalendar';
import { Segmented } from './charts';
import { nfConfirm } from '../../components/confirm';

// Форма затраты: «Финансы → Затраты» (mode='all' — статья, название, план/факт) и расход у места
// в «Маркетинг → Ссылки и QR» (mode='marketing' — место задано контекстом, сумма за период).
// Схема 14.10: «Продвижение» в «Финансах» = общий маркетинг (то, что не привязать к ссылке) + расходы
// по местам из «Маркетинга»; одно и то же нигде не вносится дважды.

export const SCHEDULE_HINT = {
  once: 'Сумма делится поровну на дни периода: в отчёт за неделю попадёт доля этой недели.',
  weekly: 'В отчёт попадает доля по дням: сумма / 7 за каждый день.',
  monthly: 'В отчёт попадает доля по дням: сумма / число дней месяца за каждый день.',
};
// Постоянная затрата — сумма в месяц «с месяца — по месяц», без выбора «как платите» (владелец 15.10):
// разовая = один и тот же месяц в обоих полях. Хранится как monthly с 1-го числа до конца месяца «по».
const monthEnd = (ym) => { const [y, m] = ym.split('-').map(Number); return `${ym}-${String(new Date(y, m, 0).getDate()).padStart(2, '0')}`; };
const SCHEDULES = [
  { value: 'monthly', label: 'Каждый месяц' },
  { value: 'weekly', label: 'Каждую неделю' },
  { value: 'once', label: 'Разово' },
];
export const todayIso = () => new Date().toLocaleDateString('sv-SE');
const parseAmount = (v) => Number(String(v).replace(/\s/g, '').replace(',', '.'));
const MONTHS_GEN = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];

export default function ExpenseForm({ mode = 'all', keys, item, categories, targets, month, category: initialCategory, onSave, onDelete, onClose }) {
  const marketingMode = mode === 'marketing';
  const place = (keys || [])[0] || null;         // место из «Маркетинга»
  const moduleOn = !!targets;                     // модуль «Источники заказов» подключён
  const cats = categories || [];
  const [category, setCategory] = useState(marketingMode ? 'marketing' : (item?.category || initialCategory || cats[0]?.key || 'other'));
  const isMarketing = marketingMode || category === 'marketing';
  const [title, setTitle] = useState(item?.title || '');
  const [amount, setAmount] = useState(item ? String(item.amount) : '');
  // Продвижение — «как платите» и даты «с — по»; у места — только сумма за период (14.10)
  const [schedule, setSchedule] = useState(item?.schedule || 'once');
  const curMonth = todayIso().slice(0, 7);
  // Продвижение — с сегодня; новая затрата в другом месяце — с его 1-го числа
  const [from, setFrom] = useState(item?.starts_on || (month && month !== curMonth ? `${month}-01` : todayIso()));
  const [to, setTo] = useState(item?.ends_on || '');
  // Постоянная: с месяца — по месяц (пусто — пока не удалите)
  const [fromMonth, setFromMonth] = useState((item?.starts_on || '').slice(0, 7) || month || curMonth);
  const [toMonth, setToMonth] = useState((item?.ends_on || '').slice(0, 7));
  const [note, setNote] = useState(item?.note || '');
  // Правка затраты, начавшейся раньше открытого месяца: новая сумма — с 1-го числа этого месяца,
  // прошлые месяцы не меняем (аналитик 15.10: без выбора «с даты / везде»)
  const refFirst = `${month || curMonth}-01`;
  const startedBefore = !marketingMode && !!item && item.schedule !== 'once' && !!item.starts_on && item.starts_on < refFirst;
  const [busy, setBusy] = useState(false);

  const once = schedule === 'once';
  const needsTitle = category === 'other';          // своё название обязательно только у «Прочего»
  const showTitle = !isMarketing;
  // У места — без выбора «как платите»: сумма за период; старые регулярные расходы правятся как были
  const showSchedule = isMarketing && (!marketingMode || (item && item.schedule !== 'once'));
  const canSubmit = !!amount && (isMarketing ? !!from : !!fromMonth) && (!needsTitle || title.trim());
  const refName = MONTHS_GEN[Number(refFirst.slice(5, 7)) - 1];

  const submit = async (e) => {
    e.preventDefault();
    const values = isMarketing
      ? { amount: parseAmount(amount), schedule, note, starts_on: from, ends_on: to }
      : { amount: parseAmount(amount), schedule: 'monthly', note,
          starts_on: `${fromMonth}-01`, ends_on: toMonth ? monthEnd(toMonth) : '' };
    if (marketingMode) {
      if (!item && place) values.source_key = place.key;
    } else {
      values.category = category;
      if (showTitle) values.title = title.trim();
      // Новая сумма у начавшейся раньше — с 1-го числа открытого месяца (затрата режется на две);
      // конец или комментарий — правка как есть
      const amountChanged = item && parseAmount(amount) !== Number(item.amount);
      if (startedBefore && amountChanged) values.apply_from = refFirst;
    }
    setBusy(true);
    try {
      await onSave(item, values);
      onClose();
    } catch (err) { /* тост уже показан */ } finally { setBusy(false); }
  };
  const remove = async () => {
    const name = item.title || place?.title || item.label || 'затрату';
    if (!(await nfConfirm(`Удалить «${name}»? Подтверждённые факты тоже удалятся.`, { confirmText: 'Удалить' }))) return;
    try { await onDelete(item); onClose(); } catch (err) { /* тост уже показан */ }
  };

  const heading = marketingMode
    ? `Расход${place ? ` · ${place.title}` : ''}`
    : isMarketing ? 'Общий маркетинг' : (item ? 'Затрата' : 'Новая затрата');
  const catHint = cats.find(c => c.key === category)?.hint;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <form className="modal" style={{ maxWidth: 540 }} onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="modal-header"><h2 className="modal-title">{heading}</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Закрыть">×</button></div>
        <div className="modal-body an-mk-form">
          {!marketingMode && cats.length > 0 && (<>
            <span className="an-mk-label">Статья</span>
            <Select value={category} onChange={setCategory} field options={cats.map(c => ({ value: c.key, label: c.title }))} />
            {isMarketing && moduleOn ? (
              <div className="an-muted an-small">
                Здесь — общий маркетинг: то, что не привязать к ссылке (блогер без ссылки, фотосессия, SMM, печать без QR).
                Расход у места — Инстаграм, карты, листовки с QR — вносите в «Маркетинг → Ссылки и QR»: сюда он попадёт сам.
              </div>
            ) : catHint && <div className="an-muted an-small">{catHint}</div>}
          </>)}
          {showTitle && (<>
            <label htmlFor="ex-title">{needsTitle ? 'Название' : 'Название — если нужно своё'}</label>
            <input id="ex-title" className="form-input" value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder={needsTitle ? 'например: охрана' : (cats.find(c => c.key === category)?.title || '')} maxLength={120} required={needsTitle} />
            <div className="an-muted an-small">{needsTitle ? 'Так строка будет называться в затратах' : 'Пусто — строка называется по статье. Своё — если таких несколько: «Аренда склада», «Зарплата курьера»'}</div>
          </>)}
          <label htmlFor="mk-exp-amount">{isMarketing ? 'Сумма, ₽' : 'Сумма в месяц, ₽'}</label>
          <input id="mk-exp-amount" className="form-input" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="5000" autoFocus={!item} required />
          {showSchedule && (<>
            <span className="an-mk-label">Как платите</span>
            <Segmented value={schedule} onChange={setSchedule} options={SCHEDULES} />
          </>)}
          {!isMarketing ? (<>
            <span className="an-mk-label">Когда</span>
            <div className="an-mk-radio">
              <label>с <input type="month" className="form-input an-ex-month" value={fromMonth} onChange={(e) => setFromMonth(e.target.value)}
                  required disabled={startedBefore} aria-label="С месяца" />
                по <input type="month" className="form-input an-ex-month" value={toMonth} min={fromMonth} onChange={(e) => setToMonth(e.target.value)} aria-label="По месяц" /></label>
            </div>
            <div className="an-muted an-small">
              {startedBefore
                ? `Новая сумма — с ${refName}, прошлые месяцы не меняем. Ошиблись с самого начала — исправьте в первом месяце затраты.`
                : '«По» можно не ставить — каждый месяц, пока не удалите. Разовая трата — один и тот же месяц в обоих полях. Первый месяц заплатили не полностью — впишите в факт свою сумму.'}
            </div>
          </>) : (<>
            <span className="an-mk-label">{once ? 'Период' : 'Когда'}</span>
            <div className="an-mk-radio">
              {/* Даты — фирменным календарём, как в заказах, а не браузерным полем (владелец 25.10) */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span>с</span>
                <div style={{ width: 160 }}><DatePickerCalendar value={from} onChange={(v) => { setFrom(v); if (to && v > to) setTo(''); }} compact portal /></div>
                <span>по</span>
                <div style={{ width: 160 }}><DatePickerCalendar value={to} onChange={setTo} minDate={from || undefined} placeholder={once ? 'тот же день' : 'без конца'} compact portal /></div>
                {to && <button type="button" className="btn-link" style={{ fontSize: 13 }} onClick={() => setTo('')}>{once ? 'Один день' : 'Без конца'}</button>}
              </div>
            </div>
            <div className="an-muted an-small">
              {once ? 'Конец можно не ставить — тогда расход за один день. ' : 'Конец можно не ставить — пока не удалите. '}{SCHEDULE_HINT[schedule]}
              {marketingMode && ' В «Финансах → Затраты» этот расход виден в «Продвижении» — «По местам».'}
            </div>
            {/* Разовый, часть уже учтена (месяц закрыт или подтверждён): та часть не меняется,
                на остальные дни периода — остаток (владелец 25.10) */}
            {once && item?.fixed_total > 0 && (() => {
              const sum = parseAmount(amount);
              const rest = Number.isFinite(sum) ? Math.round(sum - item.fixed_total) : null;
              const rub = (v) => `${Math.round(v).toLocaleString('ru-RU')} ₽`;
              const where = item.fixed_where ? item.fixed_where[0].toUpperCase() + item.fixed_where.slice(1) : 'В прошлых месяцах';
              return (
                <div className="an-small" style={{ color: rest != null && rest < 0 ? 'var(--danger-color)' : 'var(--text-secondary)' }}>
                  {where} уже учтено {rub(item.fixed_total)} — эта часть не меняется.{' '}
                  {rest == null ? '' : rest < 0
                    ? 'Сумма меньше учтённого — так не сохранится: откройте месяц заново.'
                    : `На остальные дни периода: ${rub(rest)}.`}
                </div>
              );
            })()}
          </>)}
          <label htmlFor="mk-exp-note">Комментарий</label>
          <input id="mk-exp-note" className="form-input" value={note} onChange={(e) => setNote(e.target.value)}
            placeholder={isMarketing ? 'например: блогер Аня, пост и сторис' : 'например: договор № 12, платим до 5-го'} maxLength={200} />
        </div>
        <div className="modal-footer">
          {item && onDelete && <button type="button" className="btn btn-danger-outline" style={{ marginRight: 'auto' }} onClick={remove}>Удалить</button>}
          <button type="button" className="btn btn-secondary" onClick={onClose}>Отмена</button>
          <button type="submit" className="btn btn-primary" disabled={busy || !canSubmit}>{busy ? 'Сохраняем…' : 'Сохранить'}</button>
        </div>
      </form>
    </div>
  );
}
